import { useState, useEffect } from "react";
import Header from "./Header";
import AdminSidebar from "./Sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Search, UserPlus, Eye, Edit, Trash2, Download, Filter, MoreHorizontal } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { db, auth } from "@/firebase/config";
import { 
  collection, 
  onSnapshot, 
  doc, 
  setDoc, 
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  startAt,
  endAt,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useNavigate } from "react-router-dom";

interface Student {
  id: string;
  registrationNumber: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  email?: string;
  phone?: string;
  gender: string;
  guardians?: { name: string; phone: string }[];
  guardian?: { name: string; phone: string }[]; // legacy fallback
  createdAt: string;
  currentClassUid?: string;
  className?: string; // may be present on student documents
  photoUrl?: string; // Added during profile display feature impl
}

interface StudentsTableViewProps {
  classData: {
    grade: string;
    stream?: {
      name: string;
      roomNumber: string;
      capacity: number;
      enrolled: number;
      teacher: string | null;
      teacherId: string | null;
      currentClassTeacherUid: string | null;
      classUid: string;
      isGraduated: boolean;
    };
  };
  onBack: () => void;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

const StudentsTableView = ({ classData, onBack }: StudentsTableViewProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddingStudent, setIsAddingStudent] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [validClassUids, setValidClassUids] = useState<string[]>([]);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null);

  // Pagination State
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [pageStack, setPageStack] = useState<QueryDocumentSnapshot<DocumentData>[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 60;

  // Fetch schoolID and validate admin claims
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to view students.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
        setIsAuthLoading(false);
        return;
      }

      try {
        const token = await getIdTokenResult(user, true);
        const claims: CustomClaims = token.claims as CustomClaims;
        const affiliations: Affiliation[] = claims.affiliations || [];
        const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
        const claimSchoolID: string | undefined = claims.schoolID;

        const adminAff =
          defaultAff?.role === "admin" && defaultAff.schoolID
            ? defaultAff
            : affiliations.find((aff) => aff.role === "admin" && aff.schoolID);

        if (adminAff && adminAff.schoolID === claimSchoolID) {
          setSchoolID(adminAff.schoolID!);
          setHasValidClaims(true);
        } else {
          throw new Error("Invalid admin claims");
        }
      } catch (error) {
        console.error("Claims validation error:", error);
        toast({
          title: "Missing Permissions",
          description: "You lack permissions to view students.",
          variant: "destructive",
          duration: 5000,
        });
        navigate("/login-page");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  // Fetch valid class UIDs from Firestore
  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    const classesRef = collection(db, `schools/${schoolID}/classes`);
    const unsubscribe = onSnapshot(
      classesRef,
      (snapshot) => {
        const classUids = snapshot.docs.map((doc) => doc.id);
        setValidClassUids(classUids);
        console.log("Valid class UIDs:", classUids);
      },
      (error) => {
        console.error("Error fetching classes:", error);
        toast({
          title: "Error Loading Classes",
          description: "Failed to fetch class data.",
          variant: "destructive",
          duration: 5000,
        });
      }
    );

    return () => unsubscribe();
  }, [schoolID, isAuthLoading, hasValidClaims, toast]);

  // Fetch students from Firestore (Paginated)
  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    // Wait for classData to be ready if we are in "View" mode
    if (!isAddingStudent && !classData.stream?.classUid) return;

    let unsubscribe: () => void;

    const loadStudents = () => {
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      let q;

      if (!isAddingStudent) {
         // View Mode: Filter by Class UID
         if (searchTerm.trim()) {
            q = query(
                studentsRef, 
                where("currentClassUid", "==", classData.stream?.classUid),
                orderBy("firstName"),
                startAt(searchTerm),
                endAt(searchTerm + '\uf8ff')
            );
         } else {
            q = query(
                studentsRef, 
                where("currentClassUid", "==", classData.stream?.classUid),
                orderBy("firstName")
            );
         }
      } else {
         // Add Student Mode
         if (searchTerm.trim()) {
             q = query(
                 studentsRef, 
                 orderBy("firstName"), 
                 startAt(searchTerm), 
                 endAt(searchTerm + '\uf8ff')
             );
         } else {
            q = query(studentsRef, orderBy("firstName"));
         }
      }

      // Cursor for pagination
      if (currentPage > 1 && pageStack.length >= currentPage - 1) {
         const cursor = pageStack[currentPage - 2];
         q = query(q, startAfter(cursor), limit(itemsPerPage));
      } else {
         q = query(q, limit(itemsPerPage));
      }

      unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const studentsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Student[];
          setStudents(studentsData);
          
          if (snapshot.docs.length > 0) {
              setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
          } else {
              setLastVisible(null);
          }
        },
        (error) => {
          console.error("Error fetching students:", error);
          toast({
            title: "Error Loading Students",
            description: "Failed to fetch student data.",
            variant: "destructive",
            duration: 5000,
          });
        }
      );
    };

    loadStudents();
    return () => {
        if (unsubscribe) unsubscribe();
    };
  }, [schoolID, isAuthLoading, hasValidClaims, classData.stream?.classUid, isAddingStudent, searchTerm, currentPage, toast]);

  // Debug: Log classUid when component mounts or classData changes
  useEffect(() => {
    console.log("Class UID from classData:", classData.stream?.classUid || "undefined");
  }, [classData]);

  // Filter students based on view and search term
  const filteredStudents = students.filter((student) => {
    const matchesSearch =
      student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (isAddingStudent ? false : (student.email || "").toLowerCase().includes(searchTerm.toLowerCase()));

    if (!classData.stream?.classUid) {
      console.warn("classData.stream.classUid is undefined");
      return false;
    }

    if (isAddingStudent) {
      return (!student.currentClassUid || !validClassUids.includes(student.currentClassUid)) && matchesSearch;
    } else {
      return student.currentClassUid === classData.stream?.classUid && matchesSearch;
    }
  });

  // Extract enrollment year from createdAt
  const getEnrollmentYear = (createdAt: string) => {
    try {
      return new Date(createdAt).getFullYear().toString();
    } catch {
      return "Unknown";
    }
  };

  // Count unassigned students for capacity badge
  const unassignedStudentsCount = students.filter(
    (student) => !student.currentClassUid || !validClassUids.includes(student.currentClassUid)
  ).length;

  // Handle adding a student to the class
  const handleAddStudent = async (studentId: string, registrationNumber: string) => {
    if (!schoolID || !hasValidClaims || !auth.currentUser || !classData.stream?.classUid) {
      toast({
        title: "Authentication Error",
        description: "Missing permissions, login, or class data.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      const studentRef = doc(db, `schools/${schoolID}/students`, studentId);
      await setDoc(
        studentRef,
        {
          currentClassUid: classData.stream?.classUid,
          className: `${classData.grade} ${classData.stream?.name}`,
        },
        { merge: true }
      );
      toast({
        title: "Student Added",
        description: `Student with reg id ${registrationNumber} added to ${classData.grade} ${classData.stream?.name}.`,
        duration: 3000,
      });
      setIsAddingStudent(false);
    } catch (error: any) {
      console.error("Error adding student:", error);
      toast({
        title: "Error Adding Student",
        description: error.message || "Failed to add student to class.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  // Handle removing a student from the class
  const handleRemoveStudent = async () => {
    if (!studentToDelete || !schoolID || !hasValidClaims) {
      toast({
        title: "Error",
        description: "No student selected or missing permissions.",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    try {
      const studentRef = doc(db, `schools/${schoolID}/students`, studentToDelete.id);
      await updateDoc(studentRef, {
        currentClassUid: null,
        className: null,
      });
      toast({
        title: "Student Removed",
        description: `Student ${studentToDelete.firstName} ${studentToDelete.middleName ? studentToDelete.middleName + ' ' : ''}${studentToDelete.lastName} removed from ${classData.grade} ${classData.stream?.name}.`,
        duration: 3000,
      });
      setDeleteDialogOpen(false);
      setStudentToDelete(null);
    } catch (error: any) {
      console.error("Error removing student:", error);
      toast({
        title: "Error Removing Student",
        description: error.message || "Failed to remove student from class.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  // Show error if classUid is undefined
  if (!isAuthLoading && hasValidClaims && !classData.stream?.classUid) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <Card className="m-2 w-full max-w-md bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl overflow-hidden transition-colors">
                <CardHeader>
                  <CardTitle className="text-lg sm:text-xl text-black dark:text-white">Invalid Class Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    The class ID is missing. Please select a valid class and try again.
                  </p>
                  <Button
                    onClick={onBack}
                    className="m-2 mt-4 h-10 w-full sm:w-auto bg-[#2563EB] dark:bg-[#3B82F6] text-white hover:bg-[#1E40AF] dark:hover:bg-[#2563EB] flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Go Back
                  </Button>
                </CardContent>
              </Card>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (isAuthLoading || !hasValidClaims) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto">
          <AdminSidebar onSectionChange={() => {}} />
          <div className="flex flex-col flex-1 h-full">
            <Header />
            <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 flex justify-center items-center h-[calc(100vh-64px)]">
              <div className="flex flex-col items-center gap-4">
                <div className="relative w-16 h-16">
                  <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-200 dark:border-blue-900 rounded-full"></div>
                  <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-600 dark:border-blue-400 rounded-full border-t-transparent animate-spin"></div>
                </div>
                <p className="text-blue-600 dark:text-blue-400 font-medium animate-pulse">Loading Student Data...</p>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-roboto overflow-hidden">
        <AdminSidebar onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full overflow-hidden">
          <Header />
          <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-gray-50 dark:bg-gray-900 overflow-y-auto">
            <div className="w-full max-w-7xl mx-auto">
              <Button
                onClick={isAddingStudent ? () => setIsAddingStudent(false) : onBack}
                variant="outline"
                className="mb-6 h-10 bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31] flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4 text-gray-400" />
                {isAddingStudent ? "Back to Students" : "Back to Classes"}
              </Button>

              <div className="flex flex-col gap-6 mb-8">
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-black dark:text-white truncate">
                      {isAddingStudent
                        ? `Add to ${classData.grade} ${classData.stream?.name} Students`
                        : `${classData.grade} ${classData.stream?.name} Students`}
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                      Room {classData.stream?.roomNumber || "TBA"} • Teacher: {classData.stream?.teacher || "Not Assigned"}
                      {isAddingStudent ? "" : ` (${filteredStudents.length} students)`}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge
                        variant="secondary"
                        className="text-xs px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800/50 font-medium rounded-full"
                      >
                        {classData.grade}
                      </Badge>
                      {isAddingStudent ? (
                        <Badge
                          variant="outline"
                          className="text-xs px-2.5 py-0.5 bg-white dark:bg-[#020817] border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300"
                        >
                          Unassigned: {unassignedStudentsCount}
                        </Badge>
                      ) : (
                        <>
                          <Badge
                            variant="outline"
                            className="text-xs px-2.5 py-0.5 bg-white dark:bg-[#020817] border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300"
                          >
                            {filteredStudents.length} Students
                          </Badge>
                          <Badge
                            variant="outline"
                            className="text-xs px-2.5 py-0.5 bg-white dark:bg-[#020817] border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300"
                          >
                            Capacity: {classData.stream?.capacity || 0}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>

                  {!isAddingStudent && (
                    <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10 bg-white dark:bg-[#020817] border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 flex items-center gap-2 px-4 shadow-sm"
                      >
                        <Download className="h-4 w-4 text-gray-500" />
                        <span>Export</span>
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setIsAddingStudent(true)}
                        disabled={classData.stream?.isGraduated}
                        className="h-10 bg-blue-600 dark:bg-blue-600 text-white hover:bg-blue-700 dark:hover:bg-blue-700 flex items-center gap-2 px-4 shadow-sm"
                      >
                        <UserPlus className="h-4 w-4" />
                        <span>Add Student</span>
                      </Button>
                    </div>
                  )}
                </div>

                {/* Search Bar - Centered */}
                <div className="flex justify-center w-full">
                  <div className="flex w-full max-w-2xl items-center gap-2">
                    <div className="relative flex-1 group">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                      </div>
                      <Input
                        placeholder={
                          isAddingStudent
                            ? "Search (First Name)..."
                            : "Search (First Name)..."
                        }
                        value={searchTerm}
                        onChange={(e) => {
                             setSearchTerm(e.target.value);
                             setCurrentPage(1);
                             setPageStack([]);
                        }}
                        className="pl-10 h-11 w-full bg-white dark:bg-[#020817] border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm rounded-lg"
                      />
                    </div>
                    {searchTerm && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setSearchTerm("")}
                        className="h-11 w-11 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                        title="Clear search"
                      >
                        <Filter className="h-4 w-4" />
                        <span className="sr-only">Clear Filter</span>
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Students Table */}
              <Card className="bg-white dark:bg-[#020817] border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden shadow-sm">
                <CardHeader className="pb-0 border-b border-gray-100 dark:border-gray-800/50 px-6 py-4">
                  <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                    {isAddingStudent ? "Unassigned Student Records" : "Student Records"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table className="w-full">
                      <TableHeader>
                        <TableRow className="hover:bg-transparent dark:hover:bg-transparent border-b border-gray-200 dark:border-gray-800">
                          <TableHead className="w-[120px] text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 py-3 pl-6">Reg. No.</TableHead>
                          <TableHead className="min-w-[200px] text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 py-3">Student Name</TableHead>
                          <TableHead className="w-[80px] text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 py-3">Gender</TableHead>
                          {isAddingStudent ? (
                            <TableHead className="w-[120px] text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 py-3">Enrollment Year</TableHead>
                          ) : (
                            <>
                              <TableHead className="min-w-[180px] text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 py-3">Email</TableHead>
                              <TableHead className="min-w-[150px] text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 py-3">Guardian</TableHead>
                              <TableHead className="w-[140px] text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 py-3">Guardian Phone</TableHead>
                            </>
                          )}
                          <TableHead className="w-[60px] text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 py-3 text-center pr-6">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredStudents.map((student) => (
                          <TableRow key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 transition-colors">
                            <TableCell className="py-3 pl-6">
                              <span className="font-mono text-sm text-gray-700 dark:text-gray-300 font-medium">
                                {student.registrationNumber || <span className="text-gray-400 italic font-sans">N/A</span>}
                              </span>
                            </TableCell>
                            <TableCell className="py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-bold ring-2 ring-white dark:ring-gray-900 overflow-hidden">
                                  {student.photoUrl ? (
                                    <img src={student.photoUrl} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    ((student.firstName?.charAt(0) || "") + (student.lastName?.charAt(0) || "")).toUpperCase()
                                  )}
                                </div>
                                <span className="font-medium text-sm text-gray-900 dark:text-white">
                                  {student.firstName} {student.middleName ? student.middleName + " " : ""}{student.lastName}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="py-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                student.gender?.toLowerCase() === 'male' 
                                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' 
                                  : student.gender?.toLowerCase() === 'female'
                                  ? 'bg-pink-50 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300'
                                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                              }`}>
                                {student.gender || 'N/A'}
                              </span>
                            </TableCell>

                            {isAddingStudent ? (
                              <TableCell className="py-3">
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                  {getEnrollmentYear(student.createdAt)}
                                </span>
                              </TableCell>
                            ) : (
                              <>
                                <TableCell className="py-3">
                                  {student.email ? (
                                    <a href={`mailto:${student.email}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                                      {student.email}
                                    </a>
                                  ) : (
                                    <span className="text-xs text-gray-400 italic">Not Provided</span>
                                  )}
                                </TableCell>
                                <TableCell className="py-3">
                                  <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                                    {(student.guardians?.[0]?.name || student.guardian?.[0]?.name) || <span className="text-gray-400 italic text-xs font-normal">No Guardian</span>}
                                  </span>
                                </TableCell>
                                <TableCell className="py-3">
                                  <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {(student.guardians?.[0]?.phone || student.guardian?.[0]?.phone) || <span className="text-gray-400 italic text-xs">N/A</span>}
                                  </span>
                                </TableCell>
                              </>
                            )}
                            <TableCell className="text-center py-3 pr-6">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Actions</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align="end"
                                  className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-lg shadow-lg min-w-[160px]"
                                >
                                  <DropdownMenuItem
                                    className="hover:bg-gray-50 dark:hover:bg-[#101828] text-sm cursor-pointer py-2.5"
                                    onClick={() => navigate(`/student/${student.id}`)}
                                  >
                                    <Eye className="mr-2 h-4 w-4 text-blue-500" />
                                    View Profile
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="hover:bg-gray-50 dark:hover:bg-[#101828] text-sm cursor-pointer py-2.5"
                                    onClick={() => navigate(`/student/edit/${student.id}`)}
                                  >
                                    <Edit className="mr-2 h-4 w-4 text-green-500" />
                                    Edit Details
                                  </DropdownMenuItem>
                                  {isAddingStudent && (
                                    <DropdownMenuItem
                                      onClick={() => handleAddStudent(student.id, student.registrationNumber)}
                                      className="hover:bg-gray-50 dark:hover:bg-[#101828] text-sm cursor-pointer py-2.5"
                                    >
                                      <UserPlus className="mr-2 h-4 w-4 text-purple-500" />
                                      Add to Class
                                    </DropdownMenuItem>
                                  )}
                                  {!isAddingStudent && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setStudentToDelete(student);
                                        setDeleteDialogOpen(true);
                                      }}
                                      className="hover:bg-red-50 dark:hover:bg-red-900/10 text-sm text-red-600 dark:text-red-400 cursor-pointer py-2.5"
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Remove from Class
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {filteredStudents.length === 0 && (
                     <div className="text-center py-16">
                       <div className="bg-gray-50 dark:bg-gray-800/50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                         <Search className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                       </div>
                       <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No students found</h3>
                       <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                         {isAddingStudent 
                           ? "We couldn't find any unassigned students matching your search." 
                           : "No students exist in this class yet, or none match your search."}
                       </p>
                       <Button
                         onClick={() => setSearchTerm("")}
                         variant="outline"
                         className="bg-white dark:bg-transparent border-gray-300 dark:border-gray-700 hover:bg-gray-50"
                       >
                         Clear Filters
                       </Button>
                     </div>
                   )}
                   
                   {/* Pagination Controls */}
                   <div className="flex flex-col items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-800 mt-4">
                      <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
                         Page {currentPage} ({students.length} displayed)
                      </div>
                      <div className="flex items-center gap-2 pb-4">
                         <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                if (currentPage > 1) {
                                    setPageStack(prev => prev.slice(0, -1));
                                    setCurrentPage(prev => prev - 1);
                                }
                            }}
                            disabled={currentPage === 1}
                         >
                            Previous
                         </Button>
                         <span className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white">
                            {currentPage}
                         </span>
                         <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                if (students.length >= itemsPerPage && lastVisible) {
                                    setPageStack(prev => [...prev, lastVisible]);
                                    setCurrentPage(prev => prev + 1);
                                }
                            }}
                            disabled={students.length < itemsPerPage}
                         >
                            Next
                         </Button>
                      </div>
                   </div>
                </CardContent>
              </Card>

              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] rounded-xl">
                  <DialogHeader>
                    <DialogTitle className="text-lg text-black dark:text-white">Confirm Remove Student</DialogTitle>
                    <DialogDescription className="text-sm text-gray-600 dark:text-gray-400">
                      Are you sure you want to remove {studentToDelete?.firstName} {studentToDelete?.middleName ? studentToDelete?.middleName + ' ' : ''}{studentToDelete?.lastName} from{" "}
                      {classData.grade} {classData.stream?.name}? This will unassign them from the class but retain their data in the system.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setDeleteDialogOpen(false);
                        setStudentToDelete(null);
                      }}
                      className="m-2 h-10 w-full sm:w-auto bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={handleRemoveStudent}
                      className="m-2 h-10 w-full sm:w-auto bg-[#EF4444] dark:bg-[#F87171] text-white hover:bg-[#B91C1C] dark:hover:bg-[#B91C1C]"
                    >
                      Remove Student
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};
//for classes management
export default StudentsTableView;